import { RecordMapper } from '@/bundles/App/models/RecordMapper';
import { BaseService } from '@/bundles/App/models/BaseService';
import { ITask } from '@/bundles/Tasks/interfaces/ITask';
import { IPaginationResponse } from '@/bundles/App/interfaces/IPaginationResponse';
import { IGetTasksDTO } from '@/bundles/Tasks/dto/IGetTasksDTO';
import { TaskForCalendar } from '@/bundles/Tasks/models/TaskForCalendar';
import { ITaskCreateUpdatePayload } from '@/bundles/Tasks/interfaces/ITaskCreateUpdate';

export default class TaskService extends BaseService<ITask, RecordMapper<ITask>> {
  async createTask (task: ITaskCreateUpdatePayload): Promise<ITask> {
    try {
      const response = await this.transport.post(`/task`, task);

      return this.recordMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async updateTasksStatus ({ ids, status }: { ids: number[]; status: string }): Promise<ITask[]> {
    try {
      const response = await this.transport.patch(`/task/toggle-status`, { ids, status });

      return response.map(task => this.recordMapper.mapToModel(task));
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async archive (payload: { keys: string[]; edit_all?: boolean; }): Promise<void> {
    try {
      return this.transport.patch('/task/archive', payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async updateTask ({ task, id, edit_all }: { task: ITaskCreateUpdatePayload; id: string; edit_all?: boolean; }): Promise<ITask> {
    try {
      const response = await this.transport.put(`/task/${id}`, {
        ...task,
        edit_all,
      });

      return this.recordMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getMyTasks (params: Record<string, unknown>, controller?: AbortController): Promise<IPaginationResponse<ITask>> {
    try {
      const response = await this.transport.get(`/task/my`, params, controller);

      return {
        data: response.data.map(task => this.recordMapper.mapToModel(task)),
        total: response.total,
      };
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async rescheduleTasks (payload: { task_ids: string[]; due_date: string }): Promise<void> {
    try {
      return this.transport.patch('/task/reschedule', payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getTasksForCalendar (params: IGetTasksDTO, controller?: AbortController): Promise<IPaginationResponse<TaskForCalendar>> {
    try {
      const response = await this.transport.get(`/task/calendar`, params, controller);

      return {
        data: response.data.map(task => new TaskForCalendar(task)),
        total: response.total,
      };
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }
}
